import React, { useCallback, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SignalHigh, Wifi, BatteryFull } from 'lucide-react';
/**
 * A realistic iPhone device frame. The app renders inside the "screen",
 * which is a fixed-size, clipped, rounded container. All app chrome
 * (header, bottom nav, floating bars) should anchor to this screen,
 * not the browser viewport.
 *
 * Inside the screen the native cursor is hidden and replaced with a
 * translucent grey "touch" circle that follows the pointer and shrinks
 * on press — mimicking a finger tap on a phone.
 */
export function PhoneFrame({
  children
}: {
  children: ReactNode;
}) {
  const screenRef = useRef<HTMLDivElement | null>(null);
  const [pos, setPos] = useState({
    x: 0,
    y: 0
  });
  const [visible, setVisible] = useState(false);
  const [pressed, setPressed] = useState(false);
  const updatePos = useCallback((e: React.PointerEvent) => {
    const rect = screenRef.current?.getBoundingClientRect();
    if (!rect) return;
    setPos({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);
  return <div className="min-h-screen w-full bg-[#f2ede1] flex items-center justify-center p-4 overflow-hidden" data-id="element-35">
      {/* Device bezel */}
      <div className="relative w-[390px] h-[844px] max-h-[calc(100vh-2rem)] aspect-[390/844] bg-black rounded-[3.2rem] p-[14px] shadow-[0_0_0_2px_#1c1c1e,0_40px_80px_-20px_rgba(60,50,30,0.45)]" data-id="element-36">
        {/* Side buttons */}
        <div className="absolute -left-[3px] top-[120px] h-8 w-[3px] rounded-l bg-zinc-700" data-id="element-37" />
        <div className="absolute -left-[3px] top-[170px] h-12 w-[3px] rounded-l bg-zinc-700" data-id="element-38" />
        <div className="absolute -left-[3px] top-[230px] h-12 w-[3px] rounded-l bg-zinc-700" data-id="element-39" />
        <div className="absolute -right-[3px] top-[180px] h-16 w-[3px] rounded-r bg-zinc-700" data-id="element-40" />

        {/* Screen */}
        <div ref={screenRef} className="phone-screen relative h-full w-full overflow-hidden rounded-[2.3rem] bg-zinc-950 cursor-none" onPointerEnter={e => {
        if (e.pointerType === 'mouse') {
          updatePos(e);
          setVisible(true);
        }
      }} onPointerLeave={() => {
        setVisible(false);
        setPressed(false);
      }} onPointerMove={e => {
        if (e.pointerType === 'mouse') updatePos(e);
      }} onPointerDown={e => {
        if (e.pointerType === 'mouse') setPressed(true);
      }} onPointerUp={() => setPressed(false)} data-id="element-41">
          {/* Dynamic Island */}
          <div className="absolute top-[10px] left-1/2 -translate-x-1/2 z-[60] h-[26px] w-[100px] rounded-full bg-black" data-id="element-42" />

          {/* Status bar — designed around the island */}
          <div className="pointer-events-none absolute top-0 left-0 z-[55] flex h-11 w-full items-center justify-between px-7 text-white" data-id="element-43">
            <span className="text-sm font-semibold tracking-tight tabular-nums" data-id="element-44">
              9:41
            </span>
            <div className="flex items-center gap-1.5" data-id="element-45">
              <SignalHigh className="h-4 w-4" strokeWidth={2.5} data-id="element-46" />
              <Wifi className="h-4 w-4" strokeWidth={2.5} data-id="element-47" />
              <BatteryFull className="h-5 w-5" strokeWidth={2} data-id="element-48" />
            </div>
          </div>

          {children}

          {/* Touch cursor */}
          <AnimatePresence data-id="element-49">
            {visible && <motion.div className="pointer-events-none absolute z-[70] rounded-full" style={{
            left: pos.x,
            top: pos.y
          }} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} transition={{
            duration: 0.12
          }} data-id="element-50">
                <motion.div className="rounded-full bg-zinc-300/40 ring-1 ring-zinc-100/60 backdrop-blur-[1px]" style={{
              x: '-50%',
              y: '-50%'
            }} animate={{
              width: pressed ? 30 : 40,
              height: pressed ? 30 : 40,
              backgroundColor: pressed ? 'rgba(212,212,216,0.6)' : 'rgba(212,212,216,0.35)'
            }} transition={{
              type: 'spring',
              stiffness: 500,
              damping: 30
            }} data-id="element-51" />
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </div>;
}